import {Select} from '@wandb/weave/components/Form/Select';
import React, {useCallback, useMemo} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Input} from 'semantic-ui-react';
import {Maybe} from 'src/generated/graphql';

import * as S from './ResourceVariableForm.styles';
export type SelectOption = {
  label: string;
  value: string | number;
};

type Schema = {
  type: string;
  minimum?: number;
  maximum?: number;
  enum?: string[] | number[];
};

const INTEGER_TYPE = 'integer';
const STRING_TYPE = 'string';
const FLOAT_TYPE = 'number';

function getFullDescription(
  description: Maybe<string> | undefined,
  ruleDescription: string
) {
  if (description != null) {
    return ruleDescription + description;
  }
  return ruleDescription;
}

function getRuleDescription(
  variableType: string,
  minVal?: number,
  maxVal?: number
) {
  let message = '';
  if (variableType === INTEGER_TYPE) {
    message += 'Integer field.';
  }
  if (minVal != null) {
    message += ` Min: ${minVal}`;
  }
  if (maxVal != null) {
    message += ` Max: ${maxVal}`;
  }
  if (message.length > 0) {
    message += '.\n';
  }
  return message;
}

function validateInputForNumber(
  input: number,
  minVal: number | undefined,
  maxVal: number | undefined
) {
  if (minVal != null && maxVal != null && (input < minVal || input > maxVal)) {
    return ` between ${minVal} and ${maxVal}`;
  }
  if (minVal != null && input < minVal) {
    return ` greater than or equal to  ${minVal}`;
  }
  if (maxVal != null && input > maxVal) {
    return ` less than or equal to ${maxVal}`;
  }
  return '';
}

function validateInput(
  input: string,
  expectedType: string,
  minVal: number | undefined,
  maxVal: number | undefined
) {
  if (expectedType === INTEGER_TYPE) {
    const numInput = Number(input);
    const messagePrefix = 'This value must be an integer ';
    const messagePostfix = validateInputForNumber(numInput, minVal, maxVal);
    if (messagePostfix.length > 0 || !Number.isInteger(numInput)) {
      return messagePrefix + messagePostfix;
    }
    return;
  } else if (expectedType === FLOAT_TYPE) {
    const messagePrefix = 'This value must be a number ';
    const messagePostfix = validateInputForNumber(
      Number(input),
      minVal,
      maxVal
    );
    if (messagePostfix.length > 0) {
      return messagePrefix + messagePostfix;
    }
    return;
  } else if (expectedType === STRING_TYPE) {
    return;
  }
  return `This field has an unsupported type ${expectedType}`;
}

interface ResourceVariableFormProps {
  name: string;
  schema: string;
  value: string | number | null;
  description?: Maybe<string>;
  updateVariable: (name: string, value: string | number) => void;
}

const ResourceVariableForm: React.FC<ResourceVariableFormProps> = props => {
  const {name, schema, value, description, updateVariable} = props;
  const parsedSchema: Schema = useMemo(() => JSON.parse(schema), [schema]);
  const type = parsedSchema.type.toLowerCase();
  const minVal = parsedSchema.minimum;
  const maxVal = parsedSchema.maximum;
  const options = parsedSchema.enum?.map(o => ({
    label: o.toString(),
    value: o,
  }));

  const ruleDescription = getRuleDescription(type, minVal, maxVal);
  const fullDescription = getFullDescription(description, ruleDescription);

  const selectValue = useMemo(() => {
    return {
      label: value?.toString() ?? '',
      value: value ?? '',
    };
  }, [value]);

  const inputType = type === STRING_TYPE ? STRING_TYPE : 'number';
  const [errorMessage, setErrorMessage] = React.useState<string | null>();
  const onSelectChange = useCallback(
    (updatedValue: SelectOption | null) => {
      if (updatedValue === null) {
        return;
      }
      updateVariable(name, updatedValue.value);
    },
    [updateVariable, name]
  );

  const inputValue = useMemo(() => value?.toString() || '', [value]);
  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value === null) {
        return;
      }
      setErrorMessage(validateInput(event.target.value, type, minVal, maxVal));
      updateVariable(name, event.target.value);
    },
    [name, type, minVal, maxVal, updateVariable]
  );
  return (
    <>
      <S.ResourceVariableWrapper>
        <S.ResourceVariableTitle>{name}</S.ResourceVariableTitle>
        <S.ResourceVariableColon>:</S.ResourceVariableColon>
        {options != null && (
          <S.SelectDiv>
            <Select<SelectOption, false>
              data-test={name}
              options={options}
              value={selectValue}
              onChange={onSelectChange}
            />
          </S.SelectDiv>
        )}
        {options == null && (
          <>
            <Input
              data-test={name}
              type={inputType}
              onChange={onInputChange}
              value={inputValue}
            />
          </>
        )}
      </S.ResourceVariableWrapper>
      {errorMessage && <S.ErrorMessage>{errorMessage}</S.ErrorMessage>}
      {fullDescription.length > 0 && (
        <S.ResourceVariableDescription>
          {fullDescription}
        </S.ResourceVariableDescription>
      )}
    </>
  );
};

export default ResourceVariableForm;
